import { IBreadcrumbItem, Icon } from "@fluentui/react";
import { Card, tokens } from "@fluentui/react-components";
import { New16Regular } from "@fluentui/react-icons";
import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AppStyles } from "../components/SupportView/Styles/ExploreSupport.styles";
import { SupportReportsStyles } from "../components/SupportView/Styles/SupportReportPage.styles";
import { logEvent, logPageView } from "../helper/AppInsightsHelper";
import { CustomBreadcrumb } from "../SharedComponents/CustomBreadcrumb";
import { useGetSupportReportsListQuery } from "../slices/ExploreProducts.slice";
import { ContactMethod } from "./ExploreSupport";
import MCSReportCatalog from "./MCSReportCatalog";

const SupportReports = () => {
  const [searchParams] = useSearchParams();
  const category = searchParams.get("category");
  const navigate = useNavigate();
  const { data, isLoading, isError } = useGetSupportReportsListQuery(null);

  // Check if this is MCS category
  const isMCSCategory = category === "MCS";

  const breadcrumbProps = {
    items: [
      { text: "Home", key: "explore-products-to-home", onClick: () => navigate("/") },
      { text: "Explore Reports", key: "explore-products-list", onClick: () => navigate("/explorereports?tab=supportCatalog") },
      { text: category, key: "support-reports" }
    ] as IBreadcrumbItem[],
    styles: {
      root: {
        fontSize: 14,
        margin: 0,
        paddingLeft: 8,
        "--itemLink-color": "rgb(0, 120, 212)"
      },
      item: {
        fontSize: "inherit"
      },
      itemLink: {
        fontSize: "inherit",
        color: "var(--itemLink-color)",
        ":hover": {
          background: "none",
          textDecoration: "underline",
          color: "var(--itemLink-color)"
        }
      }
    }
  };

  // Define reports for each category
  const reportsData: Record<string, any[]> = {};

  if (data && Array.isArray(data) && category) {
    reportsData[category] = data
      .filter((report: any) => report.scenarios === category)
      .map((report: any) => ({
        title: report.reportName,
        icon: "report",
        reportLink: report.reportLink
      }));
  }

  const reports = reportsData[category || ""] || [];

  const customProps = {
    Category: category
  };

  useEffect(() => {
    if (!isMCSCategory) {
      logPageView("Support Reports Page", window.location.href, customProps);
    }
  }, [isMCSCategory]);

  // MCS Category - Render dedicated component
  if (isMCSCategory) {
    return <MCSReportCatalog apiData={data} isLoading={isLoading} />;
  }

  // Default - Non-MCS categories (original design)
  return (
    <div className={AppStyles.minHeightScreen}>
      {/* Header */}
      <div className={SupportReportsStyles.header}>
        <div className={SupportReportsStyles.headerContainer}>
          <div className={SupportReportsStyles.headerContent}>
            <div style={{ display: "flex", width: "100%", justifyContent: "space-between" }}>
              <CustomBreadcrumb {...breadcrumbProps} />

              <div
                style={{
                  marginTop: "10px",
                  marginRight: "16px",
                  backgroundColor: "#0078D4",
                  color: "#FFFFFF",
                  padding: "4px 12px",
                  borderRadius: "9999px",
                  display: "inline-flex",
                  alignItems: "center",
                  fontSize: "12px",
                  fontWeight: 500
                }}
              >
                <New16Regular fontSize={12} style={{ marginRight: 4 }} />
                <span>Beta</span>
              </div>
            </div>
            <div className={SupportReportsStyles.badge}>Reports</div>
            <div className={SupportReportsStyles.mainTitle}>{category || "Reports"}</div>
            <p className={SupportReportsStyles.mainDescription}>
              {data?.find((item: any) => item.scenarios === category)?.oneliner || ""}
            </p>
          </div>
        </div>
      </div>

      {/* Reports Grid */}
      <div className={SupportReportsStyles.supportOptionsContainer}>
        <div className={SupportReportsStyles.responsiveGrid}>
          {isLoading ? (
            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 300
              }}
            >
              <div
                className="loader"
                style={{
                  border: "8px solid #f3f3f3",
                  borderTop: "8px solid #0078d4",
                  borderRadius: "50%",
                  width: 60,
                  height: 60,
                  animation: "spin 1s linear infinite"
                }}
              />
              <style>
                {`
                  @keyframes spin {
                    0% { transform: rotate(0deg);}
                    100% { transform: rotate(360deg);}
                  }
                `}
              </style>
            </div>
          ) : (
            reports.map((report, index) => <SupportReportCard key={index} {...report} isError={isError} />)
          )}
        </div>
      </div>

      {/* Immediate Assistance */}
      <div className={AppStyles.immediateAssistance}>
        <ContactMethod />
      </div>
    </div>
  );
};

// Report Card Component
const SupportReportCard = ({ title, reportLink, isError }: any) => {
  const [searchParams] = useSearchParams();
  const category = searchParams.get("category");
  const navigate = useNavigate();

  if (isError)
    return <Card style={{ margin: tokens.spacingVerticalL }}>Error loading data</Card>;

  const onReportSelected = (reportLink: string) => {
    const reportParser = reportLink.includes("/reports") ? reportLink.split("/reports")[1] : null;
    const reportId = reportParser ? reportParser.split("/")[1] : null;
    if (reportId) {
      navigate(`/supportreportview/reportid=${reportId}`, { state: { reportLink: reportLink, reportName: title, category: category } });
    } else {
      // For non-standard links, open directly
      window.open(reportLink, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div className={SupportReportsStyles.supportCard}>
      <div className={SupportReportsStyles.supportReportCardContent}>
        <div className={SupportReportsStyles.supportReportCardHeader}>
          <div className={SupportReportsStyles.supportReportCardIcon}>
            <Icon iconName="ReportDocument" style={{ fontSize: "20px", color: "white" }} />
          </div>
          <div>
            <h3
              style={{
                ...(typeof AppStyles.supportCardTitle === "object" ? AppStyles.supportCardTitle : {}),
                margin: "0",
                fontSize: "18px",
                fontWeight: "600"
              }}
            >
              {title}
            </h3>
          </div>
        </div>

        <div
          className={SupportReportsStyles.viewReportRow}
          onClick={() => {
            logEvent(`${title} report is viewed in ${category} Category`, "Support Reports Page", "SupportReportsPage");
            if (!reportLink) return;

            const isPowerBiReport = reportLink.includes("powerbi.com");

            if (isPowerBiReport) {
              onReportSelected(reportLink);
            } else {
              // Logging a bit differently here -
              // We're logging Support Report View PageView event here because we're opening the report in a new tab,
              // so we want to make sure we capture that report click.
              // Report Id will be empty here because we don't have a way to parse that from the link for non-PowerBI reports
              // Open the report link directly to maintain the user-gesture chain
              window.open(reportLink, "_blank", "noopener,noreferrer");
              // Log the page view event after opening the report
              setTimeout(() => {
                logPageView("Support Report View Page", reportLink, { ReportId: "", ReportName: title, Category: category, ReportLink: reportLink });
              }, 100);
            }
          }}
        >
          View Report
          <Icon iconName="ChevronRight" style={{ marginLeft: "8px", fontSize: "14px" }} />
        </div>
      </div>
    </div>
  );
};

export default SupportReports;
